import React, { Component } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { withTranslation } from "react-i18next";

import SafeImage from "@/js/components/SafeImage";
import { CloseButton } from "@/js/components/shared/CloseButton";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { extractHostname } from "@/js/components/RegularClassroomUser/utils";
import BlankFavicon from "@/assets/studentTile/favicons/blank-favicon.svg?url";
import { useSignatureStore } from "@/stores/v2/signatureStore";

class StudentTileTabItem extends Component {
	constructor(props) {
		super(props);
		this.state = {
			isClosed: false,
			unblockOpen: false,
			infoOpen: false,
			blockUserOpen: false,
			isCurrentlyBlocked: useClassroomPolicyStore
				.getState()
				.isCurrentlyBlocked(extractHostname(this.props.item.url), this.props.user.username),
		};
		this.unsubClassroomPolicyStore = () => {};
	}

	componentDidMount() {
		this.unsubClassroomPolicyStore = useClassroomPolicyStore.subscribe(
			(state) => state,
			(newState) => {
				this.onChange(newState);
			}
		);
	}

	componentWillUnmount() {
		this.unsubClassroomPolicyStore();
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	onChange = (state) => {
		this.setState({
			isCurrentlyBlocked: useClassroomPolicyStore
				.getState()
				.isCurrentlyBlocked(extractHostname(this.props.item.url), this.props.user.username),
		});
	};

	handleClosed = () => {
		this.props.forceCloseTab(this.props.item.tab_id);
		this.setState({
			isClosed: true,
		});
	};

	disableCloseTab = () => {
		return !this.props.item.tab_id || this.state.isClosed || !this.props.classIsActive;
	};

	closeShowInfo = () => {
		this.setState({
			infoOpen: false,
		});
	};

	/**
	 * Opens the Live View modal
	 * @returns {void}
	 */
	openInfoModal = () => {
		if (this.props.isRestricted) return;

		if (this.props.openP2pModal) {
			this.props.openP2pModal(this.props.user, this.props.item.tab_id, this.props.chromeId, this.props.chromeWindowId, false);
			return;
		}

		this.setState({
			infoOpen: true,
		});
	};

	render() {
		const signature = useSignatureStore.getState().lookupName(this.props.item.title);

		/**
		 * Handles the click event on the tab item
		 * @param {boolean} isMonitorMode - Whether the classroom is in monitor mode
		 * @param {boolean} isStudentClaimed - Whether the student is claimed or not
		 * @param {func} openInfoModal - Function to open the info modal
		 * @returns {void}
		 */
		function handleItemClick(isMonitorMode, isStudentClaimed, openInfoModal) {
			if (isMonitorMode || !isStudentClaimed) return;
			openInfoModal();
		}

		const isChromeInternal = this.props.item.url?.startsWith("chrome") || this.props.item.url?.startsWith("edge");
		const isTabClickable = !this.props.isMonitorMode && !this.props.isRestricted && this.props.isStudentClaimed;

		return (
			<div
				className={clsx(
					!this.state.isClosed ? "FastWindowTabItem" : "FastWindowTabItem--closed",
					this.props.isRestricted && "FastWindowTabItem--NoClick"
				)}
			>
				<div className="FastWindowTabItem_titleContainer">
					<SafeImage
						styleClassName={clsx(
							this.props.isMonitorMode || !this.props.isStudentClaimed
								? "tab-favicon--noInteract FastWindowTabItem_icon--noInteract"
								: "tab-favicon FastWindowTabItem_icon",
							isTabClickable && "clickable"
						)}
						src={this.props.item.favicon && !isChromeInternal ? this.props.item.favicon : BlankFavicon}
						onClick={() => handleItemClick(this.props.isMonitorMode, this.props.isStudentClaimed, this.openInfoModal)}
					/>
					<span
						className={clsx(
							this.props.isMonitorMode || !this.props.isStudentClaimed ? "FastWindowTabItem_title--noInteract" : "FastWindowTabItem_title",
							isTabClickable && "clickable"
						)}
						onClick={() => handleItemClick(this.props.isMonitorMode, this.props.isStudentClaimed, this.openInfoModal)}
						title={signature}
					>
						{signature}
					</span>
				</div>
				{this.props.canCloseTab && (
					<>
						<CloseButton
							tooltipText={this.props.t("classroomDetails.closeTab")}
							tooltipId={this.props.item.tab_id + "_closeTabTooltip"}
							disabled={this.disableCloseTab()}
							onClick={() => this.handleClosed()}
						/>
					</>
				)}
			</div>
		);
	}
}

StudentTileTabItem.propTypes = {
	user: PropTypes.object,
	item: PropTypes.object,
	forceCloseTab: PropTypes.func,
	canCloseTab: PropTypes.bool,
	classIsActive: PropTypes.bool,
	isActiveTab: PropTypes.bool,
	isMonitorMode: PropTypes.bool,
	isStudentClaimed: PropTypes.bool,
	openP2pModal: PropTypes.func,
	/**
	 * Is the teacher currently restricted from seeing the screen based on the domain or app in view? (via Screen Visibility Restrictions)
	 */
	isRestricted: PropTypes.bool,
	t: PropTypes.func.isRequired,
};

export default withTranslation()(StudentTileTabItem);
